import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Globe, Network, Wifi, Signal, Radio, Satellite, 
  Activity, AlertTriangle, CheckCircle2, Clock,
  ArrowUpRight, ArrowDownRight, RefreshCw, Settings
} from 'lucide-react';

const NetworkTab = ({ timeRange }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [networkStatus, setNetworkStatus] = useState('operational');

  // Mock network data
  const networkNodes = [
    {
      id: 'HUB-001',
      name: 'Riyadh Central Hub',
      type: 'hub',
      status: 'active',
      location: { lat: 24.7136, lng: 46.6753 },
      connections: 12,
      bandwidth: '10 Gbps',
      latency: '2ms',
      uptime: '99.98%',
      load: 75
    },
    {
      id: 'HUB-002',
      name: 'Jeddah Regional Hub',
      type: 'hub',
      status: 'active',
      location: { lat: 21.4858, lng: 39.1925 },
      connections: 8,
      bandwidth: '8 Gbps',
      latency: '5ms',
      uptime: '99.95%',
      load: 65
    },
    {
      id: 'HUB-003',
      name: 'Dammam Distribution Hub',
      type: 'hub',
      status: 'warning',
      location: { lat: 26.4207, lng: 50.0888 },
      connections: 6,
      bandwidth: '6 Gbps',
      latency: '8ms',
      uptime: '99.87%',
      load: 90
    },
    {
      id: 'EDGE-001',
      name: 'Makkah Edge Node',
      type: 'edge',
      status: 'active',
      location: { lat: 21.4225, lng: 39.8262 },
      connections: 3,
      bandwidth: '2 Gbps',
      latency: '12ms',
      uptime: '99.92%',
      load: 45
    },
    {
      id: 'EDGE-002',
      name: 'Medina Edge Node',
      type: 'edge',
      status: 'inactive',
      location: { lat: 24.5247, lng: 39.5692 },
      connections: 2,
      bandwidth: '1 Gbps',
      latency: '15ms',
      uptime: '0%',
      load: 0
    }
  ];

  const networkConnections = [
    { from: 'HUB-001', to: 'HUB-002', status: 'active', bandwidth: '5 Gbps', latency: '3ms' },
    { from: 'HUB-001', to: 'HUB-003', status: 'active', bandwidth: '4 Gbps', latency: '6ms' },
    { from: 'HUB-002', to: 'EDGE-001', status: 'active', bandwidth: '2 Gbps', latency: '8ms' },
    { from: 'HUB-003', to: 'EDGE-002', status: 'inactive', bandwidth: '1 Gbps', latency: '15ms' }
  ];

  const networkMetrics = [
    { metric: 'Total Nodes', value: '5', change: '+1', trend: 'up' },
    { metric: 'Active Connections', value: '31', change: '+3', trend: 'up' },
    { metric: 'Average Latency', value: '6.8ms', change: '-0.5ms', trend: 'down' },
    { metric: 'Network Uptime', value: '99.94%', change: '+0.02%', trend: 'up' },
    { metric: 'Bandwidth Usage', value: '78%', change: '+5%', trend: 'up' },
    { metric: 'Data Transfer', value: '2.4 TB', change: '+12%', trend: 'up' }
  ];

  const getNodeStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'inactive': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getNodeTypeIcon = (type) => {
    switch (type) {
      case 'hub': return <Globe className="w-5 h-5" />;
      case 'edge': return <Wifi className="w-5 h-5" />;
      default: return <Network className="w-5 h-5" />;
    }
  };

  const getLoadColor = (load) => {
    if (load >= 90) return 'text-red-600';
    if (load >= 75) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="p-8 space-y-8">
      {/* Network Overview Header */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-3xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Network Infrastructure</h2>
            <p className="text-indigo-100">Real-time network monitoring and topology management</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">Network Operational</span>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              <RefreshCw className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Network Metrics */}
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {networkMetrics.map((metric, index) => (
          <motion.div
            key={metric.metric}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 + index * 0.1, duration: 0.5 }}
            className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20 hover:bg-white/80 transition-all duration-300"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className={`p-2 rounded-xl ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'} bg-opacity-10`}>
                  {metric.trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                </div>
                <span className="text-sm font-medium text-gray-600">{metric.metric}</span>
              </div>
              <div className={`text-sm ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {metric.change}
              </div>
            </div>
            
            <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* Network Topology */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Network Topology</h3>
            <p className="text-gray-600">Real-time network visualization</p>
          </div>
          <Network className="w-6 h-6 text-indigo-600" />
        </div>

        <div className="relative h-96 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl overflow-hidden">
          {/* Network Connections */}
          <svg className="absolute inset-0 w-full h-full">
            {networkConnections.map((connection, index) => (
              <motion.line
                key={`${connection.from}-${connection.to}`}
                x1="20%"
                y1="30%"
                x2="80%"
                y2="70%"
                stroke={connection.status === 'active' ? '#3B82F6' : '#EF4444'}
                strokeWidth="2"
                strokeDasharray={connection.status === 'active' ? 'none' : '5,5'}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1, delay: index * 0.2 }}
              />
            ))}
          </svg>

          {/* Network Nodes */}
          {networkNodes.map((node, index) => (
            <motion.div
              key={node.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{ 
                left: `${20 + (index * 15)}%`, 
                top: `${30 + (index * 10)}%` 
              }}
              onClick={() => setSelectedNode(node)}
            >
              <div className="relative group">
                <div className={`
                  w-16 h-16 rounded-full border-4 border-white shadow-lg flex items-center justify-center
                  ${getNodeStatusColor(node.status)} relative
                `}>
                  {getNodeTypeIcon(node.type)}
                  
                  {/* Pulse animation for active nodes */}
                  {node.status === 'active' && (
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-blue-400"
                      animate={{
                        scale: [1, 1.5, 1],
                        opacity: [0.8, 0, 0.8]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  )}
                </div>
                
                {/* Node Info Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap">
                    <div className="font-medium">{node.name}</div>
                    <div className="text-xs opacity-75">{node.connections} connections</div>
                  </div>
                  <div className="w-2 h-2 bg-gray-900 transform rotate-45 absolute top-full left-1/2 -translate-x-1/2 -mt-1"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Node Details */}
        {selectedNode && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mt-6 p-4 bg-white/50 rounded-2xl border border-gray-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${getNodeStatusColor(selectedNode.status)}`}></div>
                <h4 className="text-lg font-semibold text-gray-900">{selectedNode.name}</h4>
              </div>
              <button
                onClick={() => setSelectedNode(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-sm text-gray-600">Bandwidth</div>
                <div className="text-lg font-semibold text-gray-900">{selectedNode.bandwidth}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Latency</div>
                <div className="text-lg font-semibold text-gray-900">{selectedNode.latency}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Uptime</div>
                <div className="text-lg font-semibold text-gray-900">{selectedNode.uptime}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">Load</div>
                <div className={`text-lg font-semibold ${getLoadColor(selectedNode.load)}`}>
                  {selectedNode.load}%
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Network Status */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="grid grid-cols-1 lg:grid-cols-2 gap-8"
      >
        {/* Active Nodes */}
        <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Active Nodes</h3>
              <p className="text-gray-600">Real-time node status</p>
            </div>
            <Activity className="w-6 h-6 text-indigo-600" />
          </div>

          <div className="space-y-4">
            {networkNodes.filter(node => node.status === 'active').map((node, index) => (
              <motion.div
                key={node.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="flex items-center justify-between p-4 bg-white/50 rounded-xl border border-gray-200"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${getNodeStatusColor(node.status)}`}></div>
                  <div>
                    <div className="font-medium text-gray-900">{node.name}</div>
                    <div className="text-sm text-gray-600">{node.type.toUpperCase()}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{node.connections} connections</div>
                  <div className="text-xs text-gray-500">{node.bandwidth}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Network Alerts */}
        <div className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/20">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Network Alerts</h3>
              <p className="text-gray-600">Recent network events</p>
            </div>
            <AlertTriangle className="w-6 h-6 text-indigo-600" />
          </div>

          <div className="space-y-4">
            {[
              { type: 'warning', message: 'High load detected on Dammam Hub', time: '2 min ago' },
              { type: 'info', message: 'New connection established', time: '5 min ago' },
              { type: 'error', message: 'Medina Edge Node offline', time: '15 min ago' },
              { type: 'success', message: 'Network optimization completed', time: '1 hour ago' }
            ].map((alert, index) => (
              <motion.div
                key={index}
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`p-4 rounded-xl border ${
                  alert.type === 'warning' ? 'border-yellow-200 bg-yellow-50' :
                  alert.type === 'error' ? 'border-red-200 bg-red-50' :
                  alert.type === 'success' ? 'border-green-200 bg-green-50' :
                  'border-blue-200 bg-blue-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full ${
                    alert.type === 'warning' ? 'bg-yellow-500' :
                    alert.type === 'error' ? 'bg-red-500' :
                    alert.type === 'success' ? 'bg-green-500' :
                    'bg-blue-500'
                  }`}></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{alert.message}</div>
                    <div className="text-xs text-gray-500">{alert.time}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default NetworkTab; 